// @figma file=pkCGby54zXzTsccXvHqzO6 node=15217:47508 name="Accordion" set="my-taible-shadcn-studio" verified=2026-03-22

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * Visual style of the accordion container.
 *
 * default  → bottom border on each item, no outer border (shadcn default)
 * outline  → full outer border + radius wrapping all items (Figma: Variants=Outline)
 * ghost    → no borders at all — purely spacing-based separation
 */
export const accordionVariants = cva('w-full', {
  variants: {
    variant: {
      default: '',
      outline: 'border border-border rounded-[var(--radius)] overflow-hidden',
      ghost: '',
    },
  },
  defaultVariants: { variant: 'default' },
});

export const accordionItemVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b border-border last:border-b-0',
      outline: 'border-b border-border last:border-b-0',
      ghost: '',
    },
  },
  defaultVariants: { variant: 'default' },
});

// ─── Types ────────────────────────────────────────────────────────────────────

export type AccordionVariant = 'default' | 'outline' | 'ghost';

// Accordion root (single)
export interface AccordionSingleProps
  extends Omit<React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>, 'type'>,
    VariantProps<typeof accordionVariants> {
  type: 'single';
  value?: string;
  onValueChange?: (value: string) => void;
  collapsible?: boolean;
}

// Accordion root (multiple)
export interface AccordionMultipleProps
  extends Omit<React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>, 'type'>,
    VariantProps<typeof accordionVariants> {
  type: 'multiple';
  value?: string[];
  onValueChange?: (value: string[]) => void;
}

export type AccordionProps = AccordionSingleProps | AccordionMultipleProps;

export interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> {
  /** Passed down from Accordion root to apply the correct item border style. */
  _variant?: AccordionVariant;
}

export interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {}

export interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> {}

// ─── Accordion (root) ─────────────────────────────────────────────────────────

/**
 * Vertically stacked set of collapsible content sections.
 *
 * Built on `@radix-ui/react-accordion` with full ARIA support:
 * - Keyboard: `Space` / `Enter` to toggle, `Tab` to move between triggers,
 *   `Home` / `End` to jump to first / last trigger.
 * - ARIA: `aria-expanded`, `aria-controls`, `aria-labelledby` applied automatically.
 *
 * @example
 * ```tsx
 * <Accordion type="single" collapsible>
 *   <AccordionItem value="faq-1">
 *     <AccordionTrigger>Is it accessible?</AccordionTrigger>
 *     <AccordionContent>Yes. It follows the WAI-ARIA design pattern.</AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 * ```
 */
const Accordion = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Root>,
  AccordionProps
>(({ className, variant, children, ...props }, ref) => {
  // Inject _variant into AccordionItem children so they can apply the
  // matching item border style without requiring consumers to repeat the prop.
  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<AccordionItemProps>, {
        _variant: (variant ?? 'default') as AccordionVariant,
      });
    }
    return child;
  });

  return (
    <AccordionPrimitive.Root
      ref={ref}
      className={cn(accordionVariants({ variant }), className)}
      {...(props as React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>)}
    >
      {enhancedChildren}
    </AccordionPrimitive.Root>
  );
});
Accordion.displayName = 'Accordion';

// ─── AccordionItem ────────────────────────────────────────────────────────────

const AccordionItem = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, _variant = 'default', ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(accordionItemVariants({ variant: _variant }), className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

// ─── AccordionTrigger ─────────────────────────────────────────────────────────

const AccordionTrigger = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        // Layout
        'flex flex-1 items-center justify-between',
        // Typography
        'py-4 px-0 text-sm font-medium text-foreground',
        // Interaction
        'cursor-pointer transition-all',
        'hover:underline',
        // Focus ring — uses design-system ring token
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-[var(--radius)]',
        // Disabled
        'disabled:pointer-events-none disabled:opacity-50',
        // Chevron rotation on open
        '[&[data-state=open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronDownIcon
        className="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
        aria-hidden="true"
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = 'AccordionTrigger';

// ─── AccordionContent ─────────────────────────────────────────────────────────

const AccordionContent = React.forwardRef<
  React.ComponentRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      // Animate open / close via Radix data attributes
      'overflow-hidden text-sm',
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      className,
    )}
    {...props}
  >
    {/* Inner div provides the padding so animation clips cleanly */}
    <div className="pb-4 pt-0 text-foreground">{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = 'AccordionContent';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
